import { useEffect, useMemo, useState, type InputHTMLAttributes } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiEdit2,
  FiLoader,
  FiLock,
  FiShare2,
  FiTag,
  FiTrash2,
  FiUserPlus,
  FiX,
  FiXCircle,
} from 'react-icons/fi';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { Button } from '../components/ui/Button';
import { Avatar } from '../components/ui/Avatar';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { useAuth } from '../hooks/useAuth';
import { useEventAccentColor } from '../hooks/useEventAccentColor';
import { useGetEventDetail, useGetPublicEventDetail } from '../api/events';
import { formatDate, formatTime } from '../utils/eventDate';
import { copyTextToClipboard } from '../utils/copyToClipboard';
import { toast } from '../utils/toast';
import { extractApiError, parseApiError } from '../utils/apiError';
import {
  MAX_TICKETS_PER_PURCHASE,
  CHECKOUT_EMAIL_STORAGE_KEY,
  CHECKOUT_NAME_STORAGE_KEY,
  buildPaymentReturnUrl,
  calculateCheckoutSummary,
  cancelActiveCheckoutSession,
  checkActiveCheckoutSession,
  checkFreeBookingEligibility,
  clearStoredCheckoutSession,
  createPublicCheckoutSession,
  createPublicFreeBooking,
  fetchTicketAvailability,
  getCheckoutActiveSession,
  getCheckoutClientSecret,
  getCheckoutPaymentStatus,
  getCheckoutPublishableKey,
  getCheckoutSessionId,
  getCheckoutStatusValue,
  getEmbeddedCheckoutSessionStatus,
  getPublicCheckoutSessionStatus,
  getReservationExpiryDate,
  getStoredCheckoutCustomer,
  getStoredCheckoutSession,
  resumeCheckoutSession,
  storeCheckoutCustomer,
  storeCheckoutSession,
  validatePromoCode,
  type ActiveSessionInfo,
  type CheckoutRecipient,
  type CheckoutSessionResponse,
  type CheckoutSessionStatusResponse,
  type PromoCodeValidationResponse,
  type TicketAvailabilityItem,
} from '../api/checkout';
import type { EventItem, EventTicket } from '../api/events';

const EVENT_IMAGE_FALLBACK_SRC = '/Musicade-Logo.png';

const checkoutSearchSchema = z.object({
  ticketId: z.coerce.number().optional(),
  tickets: z.string().optional(),
  inviteToken: z.string().optional(),
  session_id: z.string().optional(),
});

export const Route = createFileRoute('/events/$eventId/checkout')({
  validateSearch: (search) => checkoutSearchSchema.parse(search),
  component: EventCheckoutPage,
});

// ─── Helpers ────────────────────────────────────────────────────────────────

interface RecipientEntry {
  id: number;
  name: string;
  email: string;
  ticketId: number | null;
  quantity: number;
}

type CheckoutView = 'selection' | 'embedded' | 'processing' | 'success' | 'failure';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(email: string) {
  return emailRegex.test(email.trim());
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatTimeRemaining(timeRemainingMs: number) {
  if (timeRemainingMs <= 0) return '0:00';
  const minutes = Math.floor(timeRemainingMs / 60000);
  const seconds = Math.floor((timeRemainingMs % 60000) / 1000);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function normaliseAvailability(items?: TicketAvailabilityItem[]) {
  return (items ?? []).reduce<Record<number, number>>((accumulator, item) => {
    const ticketId = item.id ?? item.ticket_id;
    const remaining = item.remaining_tickets ?? item.available;
    if (typeof ticketId === 'number' && typeof remaining === 'number') {
      accumulator[ticketId] = remaining;
    }
    return accumulator;
  }, {});
}

/** Parses the "id:qty,id:qty" cart search param into a ticketCounts map. */
function parseTicketsParam(param?: string): Record<number, number> {
  if (!param) return {};
  return param.split(',').reduce<Record<number, number>>((accumulator, pair) => {
    const [idPart, qtyPart] = pair.split(':');
    const id = Number(idPart);
    const qty = Number(qtyPart);
    if (Number.isFinite(id) && Number.isFinite(qty) && qty > 0) {
      accumulator[id] = qty;
    }
    return accumulator;
  }, {});
}

function FormField({
  label,
  className,
  ...props
}: { label: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">
        {label}
      </span>
      <input
        {...props}
        className={`w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-white outline-none placeholder:text-zinc-600 focus:border-(--event-accent)/50 disabled:opacity-60 ${className ?? ''}`}
      />
    </label>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

function EventCheckoutPage() {
  const { eventId } = Route.useParams();
  const {
    ticketId,
    tickets: ticketsParam,
    inviteToken,
    session_id: returnedSessionId,
  } = Route.useSearch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const authEventResult = useGetEventDetail(isAuthenticated ? eventId : undefined);
  const publicEventResult = useGetPublicEventDetail(!isAuthenticated ? eventId : undefined);
  const { data: event, isLoading, isError } = isAuthenticated ? authEventResult : publicEventResult;

  const goToEvent = () => navigate({ to: '/events/$eventId', params: { eventId } });

  if (isLoading) {
    return <LoadingLogo />;
  }

  if (isError || !event) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="w-full rounded-4xl border border-white/8 bg-zinc-950/90 p-8 sm:p-12 text-center shadow-2xl flex flex-col items-center gap-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center bg-red-500/10">
              <FiAlertCircle size={40} className="text-red-400" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Event not found</h2>
              <p className="text-zinc-400 text-sm sm:text-base max-w-sm mx-auto">
                This event may have been removed, or you don't have permission to view it.
              </p>
            </div>
            <Button
              onClick={() => navigate({ to: '/events' })}
              size="lg"
              className="rounded-full px-8"
            >
              <FiArrowLeft size={16} className="mr-2" />
              Back to Events
            </Button>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <CheckoutContent
      event={event}
      eventId={eventId}
      ticketId={ticketId}
      ticketsParam={ticketsParam}
      inviteToken={inviteToken ?? null}
      returnedSessionId={returnedSessionId ?? null}
      goToEvent={goToEvent}
    />
  );
}

// ─── Checkout content (has real event data available) ─────────────────────

function CheckoutContent({
  event,
  eventId,
  ticketId,
  ticketsParam,
  inviteToken,
  returnedSessionId,
  goToEvent,
}: {
  event: EventItem;
  eventId: string;
  ticketId?: number;
  ticketsParam?: string;
  inviteToken: string | null;
  returnedSessionId: string | null;
  goToEvent: () => void;
}) {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const storedCustomer = getStoredCheckoutCustomer();
  const eventAccent = useEventAccentColor(event.dominant_color);

  const getSeededTicketCounts = () => {
    const fromCart = parseTicketsParam(ticketsParam);
    if (Object.keys(fromCart).length > 0) return fromCart;
    return ticketId ? { [ticketId]: 1 } : {};
  };

  const [ticketCounts] = useState<Record<number, number>>(getSeededTicketCounts);
  const [buyerName, setBuyerName] = useState(storedCustomer.name);
  const [buyerEmail, setBuyerEmail] = useState(storedCustomer.email);
  const [availabilityMap, setAvailabilityMap] = useState<Record<number, number>>({});
  const [checkoutView, setCheckoutView] = useState<CheckoutView>('selection');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [activeSessionInfo, setActiveSessionInfo] = useState<ActiveSessionInfo | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutSessionStatusResponse | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [reservationExpiresAt, setReservationExpiresAt] = useState<Date | null>(null);
  const [timeRemainingMs, setTimeRemainingMs] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecovering, setIsRecovering] = useState(true);

  // Recipient state — each entry is a single (name, email, ticket type) assignment,
  // matching the existing one-ticket-per-recipient-entry model used by the API.
  const [recipients, setRecipients] = useState<RecipientEntry[]>([]);
  const [isRecipientFormOpen, setIsRecipientFormOpen] = useState(false);
  const [draftRecipient, setDraftRecipient] = useState<RecipientEntry | null>(null);
  const [draftRecipientError, setDraftRecipientError] = useState<string | null>(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [promoDiscount, setPromoDiscount] = useState<PromoCodeValidationResponse | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoValidating, setPromoValidating] = useState(false);

  // Presentational-only email preferences — no backend field exists for these yet.
  const [emailOptInOrganizer, setEmailOptInOrganizer] = useState(true);
  const [emailOptInMusicade, setEmailOptInMusicade] = useState(true);

  function resetSessionState() {
    clearStoredCheckoutSession();
    localStorage.removeItem(CHECKOUT_EMAIL_STORAGE_KEY);
    localStorage.removeItem(CHECKOUT_NAME_STORAGE_KEY);
    setSessionId(null);
    setClientSecret(null);
    setStripePromise(null);
    setReservationExpiresAt(null);
    setTimeRemainingMs(0);
    setActiveSessionInfo(null);
    setCheckoutStatus(null);
    setCheckoutError(null);
    setIsSubmitting(false);
  }

  const tickets = useMemo(
    () => (event.tickets ?? []).filter((t) => !t.is_archived && typeof t.id === 'number'),
    [event.tickets],
  );

  const getTicketSalesStatus = (ticket: EventTicket): 'available' | 'not_started' | 'ended' => {
    const now = Date.now();
    if (ticket.expired_at) {
      const expiredAt = new Date(ticket.expired_at).getTime();
      if (!Number.isNaN(expiredAt) && expiredAt <= now) return 'ended';
    }
    if (!ticket.use_publish_time_for_start_at && ticket.start_at) {
      const startsAt = new Date(ticket.start_at).getTime();
      if (!Number.isNaN(startsAt) && startsAt > now) return 'not_started';
    }
    return 'available';
  };

  const selectedTickets = useMemo(
    () => tickets.filter((ticket) => (ticketCounts[ticket.id!] ?? 0) > 0),
    [ticketCounts, tickets],
  );

  const totalTickets = useMemo(
    () => Object.values(ticketCounts).reduce((sum, count) => sum + count, 0),
    [ticketCounts],
  );

  const subtotal = useMemo(
    () =>
      selectedTickets.reduce(
        (sum, ticket) => sum + ticket.price * (ticketCounts[ticket.id!] ?? 0),
        0,
      ),
    [selectedTickets, ticketCounts],
  );

  const summary = useMemo(() => calculateCheckoutSummary(subtotal), [subtotal]);
  const allSelectedTicketsFree =
    selectedTickets.length > 0 && selectedTickets.every((ticket) => ticket.price === 0);

  const promoDiscountAmount = useMemo(() => {
    if (!appliedPromoCode || !promoDiscount) return 0;
    const raw = promoDiscount.discount_amount ?? promoDiscount.total_discount ?? 0;
    return Math.max(0, parseFloat(String(raw)) || 0);
  }, [appliedPromoCode, promoDiscount]);

  const discountedSubtotal = useMemo(
    () => Math.max(0, subtotal - promoDiscountAmount),
    [subtotal, promoDiscountAmount],
  );

  const discountedSummary = useMemo(
    () => calculateCheckoutSummary(discountedSubtotal),
    [discountedSubtotal],
  );

  // Availability conflicts — the cart was decided on the previous page, so if
  // stock shifted in the meantime we surface it here rather than letting it
  // silently fail at submit time.
  function getRemainingTickets(ticket: EventTicket) {
    const ticketId2 = ticket.id;
    if (!ticketId2) return undefined;
    if (typeof availabilityMap[ticketId2] === 'number') return availabilityMap[ticketId2];
    return ticket.remaining_tickets;
  }

  const salesClosedReason = useMemo<{ notStarted: string | null; ended: boolean }>(() => {
    const hasActive = selectedTickets.some((t) => getTicketSalesStatus(t) === 'available');
    if (selectedTickets.length === 0 || hasActive) return { notStarted: null, ended: false };

    const notStartedTickets = selectedTickets.filter(
      (t) => getTicketSalesStatus(t) === 'not_started',
    );
    if (notStartedTickets.length > 0) {
      const startTimes = notStartedTickets
        .map((t) => new Date(t.start_at!).getTime())
        .filter((t) => !Number.isNaN(t));
      const notStartedAt =
        startTimes.length > 0 ? new Date(Math.min(...startTimes)).toISOString() : null;
      return { notStarted: notStartedAt, ended: false };
    }
    return { notStarted: null, ended: true };
  }, [selectedTickets]);

  const salesClosed = salesClosedReason.notStarted !== null || salesClosedReason.ended;

  const hasAvailabilityConflict = useMemo(
    () =>
      selectedTickets.some((t) => {
        const remaining = getRemainingTickets(t);
        return typeof remaining === 'number' && (ticketCounts[t.id!] ?? 0) > remaining;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTickets, availabilityMap, ticketCounts],
  );

  // Reset promo code whenever the cart changes
  useEffect(() => {
    if (appliedPromoCode) {
      setAppliedPromoCode(null);
      setPromoDiscount(null);
      setPromoError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleValidatePromoCode() {
    if (!promoCode.trim() || !event?.id) return;
    setPromoValidating(true);
    setPromoError(null);
    const cartItems = selectedTickets.map((t) => ({
      ticket_id: t.id!,
      quantity: ticketCounts[t.id!] ?? 0,
    }));
    try {
      const response = await validatePromoCode(event.id, promoCode.trim(), cartItems);
      // Invalid promo codes are HTTP 400 with a PROMO_* error_code, handled in the catch.
      setAppliedPromoCode(promoCode.trim().toUpperCase());
      setPromoDiscount(response);
      setPromoError(null);
    } catch (err: unknown) {
      setPromoError(extractApiError(err, 'Failed to validate promo code. Please try again.'));
      setAppliedPromoCode(null);
      setPromoDiscount(null);
    } finally {
      setPromoValidating(false);
    }
  }

  function handleRemovePromoCode() {
    setPromoCode('');
    setAppliedPromoCode(null);
    setPromoDiscount(null);
    setPromoError(null);
  }

  const eventTitle = event.event_title || 'Event checkout';

  useEffect(() => {
    let cancelled = false;

    const bootstrapCheckout = async () => {
      try {
        await refreshAvailability();

        if (returnedSessionId) {
          await resolveReturnedSession(returnedSessionId, cancelled);
          return;
        }

        await hydrateFromStoredSession(cancelled);
      } finally {
        if (!cancelled) setIsRecovering(false);
      }
    };

    void bootstrapCheckout();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!reservationExpiresAt) return undefined;
    const interval = window.setInterval(() => {
      const timeLeft = reservationExpiresAt.getTime() - Date.now();
      if (timeLeft <= 0) {
        setTimeRemainingMs(0);
        handleExpiredReservation();
        return;
      }
      setTimeRemainingMs(timeLeft);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [reservationExpiresAt]);

  useEffect(() => {
    if (checkoutView !== 'embedded' || !sessionId) return undefined;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'You have active ticket reservations. Are you sure you want to leave?';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [checkoutView, sessionId]);

  useEffect(() => {
    if (checkoutView !== 'embedded' || !sessionId) return undefined;
    const interval = window.setInterval(() => {
      void pollCheckoutStatus(sessionId);
    }, 2000);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutView, sessionId]);

  async function refreshAvailability() {
    try {
      const availability = await fetchTicketAvailability(event.id);
      const items = availability.tickets ?? availability.availability ?? availability.results ?? [];
      setAvailabilityMap(normaliseAvailability(items));
    } catch {
      setAvailabilityMap({});
    }
  }

  function validateCheckout() {
    if (totalTickets === 0) {
      setCheckoutError('No tickets selected. Head back and pick your tickets first.');
      return false;
    }
    if (!buyerName.trim()) {
      setCheckoutError('Enter the buyer name for this order.');
      return false;
    }
    if (!isValidEmail(buyerEmail)) {
      setCheckoutError('Enter a valid buyer email address.');
      return false;
    }
    if (totalTickets > MAX_TICKETS_PER_PURCHASE) {
      setCheckoutError(
        `You can only purchase up to ${MAX_TICKETS_PER_PURCHASE} tickets per order.`,
      );
      return false;
    }
    if (hasAvailabilityConflict) {
      setCheckoutError('One or more selected tickets are no longer available in this quantity.');
      return false;
    }

    // Duplicate email checks — the buyer can't also be a gift recipient, and two
    // *different* recipients can't share an email. The same person is allowed to
    // hold several entries (one per ticket type) under one email, as long as the
    // name matches across all of them.
    const normalizedBuyerEmail = buyerEmail.trim().toLowerCase();
    const emailToNames = new Map<string, Set<string>>();
    for (const recipient of recipients) {
      const normalizedRecipientEmail = recipient.email.trim().toLowerCase();
      if (!normalizedRecipientEmail) continue;
      if (normalizedRecipientEmail === normalizedBuyerEmail) {
        setCheckoutError(
          `Recipient "${recipient.name || recipient.email}" has the same email as the buyer. Use a different email for gift recipients.`,
        );
        return false;
      }
      const normalizedName = recipient.name.trim().toLowerCase();
      const names = emailToNames.get(normalizedRecipientEmail) ?? new Set<string>();
      names.add(normalizedName);
      emailToNames.set(normalizedRecipientEmail, names);
      if (names.size > 1) {
        setCheckoutError(
          `Multiple different recipients share the email "${recipient.email}". Each recipient needs a unique email.`,
        );
        return false;
      }
    }

    const assignedQuantityByTicket: Record<number, number> = {};
    for (const recipient of recipients) {
      if (!recipient.name.trim() || !recipient.email.trim() || !recipient.ticketId) {
        setCheckoutError('All recipients must have a name, email, and an assigned ticket type.');
        return false;
      }
      if (!isValidEmail(recipient.email)) {
        setCheckoutError(
          `Invalid email for recipient "${recipient.name}". Please fix before continuing.`,
        );
        return false;
      }
      assignedQuantityByTicket[recipient.ticketId] =
        (assignedQuantityByTicket[recipient.ticketId] ?? 0) + recipient.quantity;
    }
    for (const [ticketIdKey, assignedQuantity] of Object.entries(assignedQuantityByTicket)) {
      const ticketId = Number(ticketIdKey);
      const selectedCount = ticketCounts[ticketId] ?? 0;
      if (assignedQuantity > selectedCount) {
        const ticketLabel =
          tickets.find((t) => t.id === ticketId)?.ticket_type ?? 'that ticket type';
        setCheckoutError(
          `You selected ${selectedCount} ticket(s) of "${ticketLabel}", but assigned ${assignedQuantity} to recipients.`,
        );
        return false;
      }
    }

    setCheckoutError(null);
    return true;
  }

  function openAddRecipientForm() {
    setDraftRecipient({ id: Date.now(), name: '', email: '', ticketId: null, quantity: 1 });
    setDraftRecipientError(null);
    setIsRecipientFormOpen(true);
  }

  function openEditRecipientForm(recipient: RecipientEntry) {
    setDraftRecipient({ ...recipient });
    setDraftRecipientError(null);
    setIsRecipientFormOpen(true);
  }

  function closeRecipientForm() {
    setIsRecipientFormOpen(false);
    setDraftRecipient(null);
    setDraftRecipientError(null);
  }

  /** Shared validation for a recipient draft — used by Save and Save & add another. */
  function validateRecipientDraft(draft: RecipientEntry): string | null {
    const { name, email, ticketId, quantity } = draft;
    if (!name.trim() || !email.trim() || !ticketId) return 'Please fill in all fields.';
    if (!isValidEmail(email)) return 'Please enter a valid email address.';

    const normalizedEmail = email.trim().toLowerCase();
    if (normalizedEmail === buyerEmail.trim().toLowerCase()) {
      return 'This recipient email matches the buyer email. Use a different email for gift recipients.';
    }
    // Same email + same name = another ticket for the same person, which is
    // allowed (grouped into one card). Same email with a different name means
    // two distinct people were accidentally given one email — that's blocked.
    const normalizedName = name.trim().toLowerCase();
    const emailUsedByDifferentPerson = recipients.some(
      (r) =>
        r.id !== draft.id &&
        r.email.trim().toLowerCase() === normalizedEmail &&
        r.name.trim().toLowerCase() !== normalizedName,
    );
    if (emailUsedByDifferentPerson) {
      return 'This email is already assigned to a different recipient. Each recipient needs a unique email.';
    }

    const selectedCount = ticketCounts[ticketId] ?? 0;
    const currentUsage = recipients
      .filter((r) => r.ticketId === ticketId && r.id !== draft.id)
      .reduce((sum, r) => sum + r.quantity, 0);
    const available = selectedCount - currentUsage;

    if (!Number.isInteger(quantity) || quantity < 1) {
      return 'Enter a valid quantity.';
    }
    if (quantity > available) {
      const ticketLabel = tickets.find((t) => t.id === ticketId)?.ticket_type ?? 'that ticket type';
      return `You have only selected ${selectedCount} ticket(s) of "${ticketLabel}", and ${available} remain unassigned. Lower the quantity or add more tickets first.`;
    }
    return null;
  }

  function commitRecipientDraft(draft: RecipientEntry): boolean {
    const error = validateRecipientDraft(draft);
    if (error) {
      setDraftRecipientError(error);
      return false;
    }
    setRecipients((prev) => {
      const exists = prev.find((r) => r.id === draft.id);
      return exists ? prev.map((r) => (r.id === draft.id ? draft : r)) : [...prev, draft];
    });
    setDraftRecipientError(null);
    return true;
  }

  function handleSaveRecipient() {
    if (!draftRecipient) return;
    if (commitRecipientDraft(draftRecipient)) {
      closeRecipientForm();
    }
  }

  /** Saves the current recipient, then immediately opens a new form pre-filled
   * with the same name/email — a shortcut for assigning this person a second
   * ticket type without retyping their details. */
  function handleSaveAndAddAnother() {
    if (!draftRecipient) return;
    if (!commitRecipientDraft(draftRecipient)) return;
    setDraftRecipient({
      id: Date.now(),
      name: draftRecipient.name,
      email: draftRecipient.email,
      ticketId: null,
      quantity: 1,
    });
    setDraftRecipientError(null);
  }

  function handleRemoveRecipient(id: number) {
    setRecipients((prev) => prev.filter((r) => r.id !== id));
    if (draftRecipient?.id === id) closeRecipientForm();
  }

  function buildApiRecipients(): CheckoutRecipient[] {
    return recipients.map((r) => ({
      name: r.name,
      email: r.email,
      ticket_id: r.ticketId ?? undefined,
      quantity: r.quantity,
    }));
  }

  async function startEmbeddedCheckout(response: CheckoutSessionResponse) {
    const nextClientSecret = getCheckoutClientSecret(response);
    const nextSessionId = getCheckoutSessionId(response);
    const publishableKey =
      getCheckoutPublishableKey(response) ?? import.meta.env.VITE_STRIPE_PUBLIC_KEY;
    const reservationExpiry =
      getReservationExpiryDate(response) ?? new Date(Date.now() + 10 * 60 * 1000);

    if (!nextClientSecret || !nextSessionId || !publishableKey) {
      throw new Error('Missing Stripe checkout details from the server.');
    }

    setCheckoutStatus(null);
    setActiveSessionInfo(null);
    setCheckoutError(null);
    setClientSecret(nextClientSecret);
    setSessionId(nextSessionId);
    setReservationExpiresAt(reservationExpiry);
    setTimeRemainingMs(reservationExpiry.getTime() - Date.now());
    storeCheckoutSession(nextSessionId, reservationExpiry.toISOString());

    let stripeInstance;
    try {
      stripeInstance = await loadStripe(publishableKey);
      if (!stripeInstance) throw new Error('Stripe returned null instance.');
    } catch {
      cancelActiveCheckoutSession({
        email: buyerEmail || undefined,
        session_id: nextSessionId,
      }).catch(() => {
        /* best-effort */
      });
      clearStoredCheckoutSession();
      setSessionId(null);
      setClientSecret(null);
      setReservationExpiresAt(null);
      throw new Error(
        'Failed to load the payment system. Your reservation has been released — please try again.',
      );
    }

    setStripePromise(Promise.resolve(stripeInstance));
    setCheckoutView('embedded');
  }

  async function handleResumeSession(resumeId?: string) {
    const targetSessionId = resumeId ?? activeSessionInfo?.session_id ?? sessionId;
    if (!targetSessionId) return;

    setIsSubmitting(true);
    setCheckoutError(null);

    try {
      const response = await resumeCheckoutSession(targetSessionId);
      await startEmbeddedCheckout(response);
    } catch (error) {
      const httpStatus = parseApiError(error).status;
      if (httpStatus === 404 || httpStatus === 410 || httpStatus === 400) {
        clearStoredCheckoutSession();
        setActiveSessionInfo(null);
        setCheckoutError('This session is no longer available. Please start a new checkout.');
      } else {
        setCheckoutError(extractApiError(error, 'Unable to resume this checkout session.'));
      }
      setCheckoutView('selection');
    } finally {
      setIsSubmitting(false);
    }
  }

  // Stripe Embedded Checkout redirects the browser here after payment
  // (return_url) instead of opening a separate success page — resolve the
  // session and show it inline via checkoutView === 'success'.
  async function resolveReturnedSession(returnedSessionIdValue: string, cancelled: boolean) {
    try {
      const status = await getPublicCheckoutSessionStatus(returnedSessionIdValue);
      if (cancelled) return;

      const sessionStatus = getCheckoutStatusValue(status);
      setSessionId(returnedSessionIdValue);
      setCheckoutStatus(status);

      if (sessionStatus === 'complete') {
        clearStoredCheckoutSession();
        setCheckoutView('success');
      } else if (sessionStatus === 'processing') {
        setCheckoutView('processing');
      } else if (sessionStatus === 'expired') {
        clearStoredCheckoutSession();
        setCheckoutView('failure');
        setCheckoutError('Your checkout session expired before payment completed.');
      } else {
        clearStoredCheckoutSession();
        setCheckoutView('failure');
        setCheckoutError('We could not confirm your payment. Please try again.');
      }
    } catch {
      if (cancelled) return;
      setCheckoutView('failure');
      setCheckoutError(
        'We could not verify your payment status. Please check your tickets or try again.',
      );
    } finally {
      if (!cancelled) {
        void navigate({
          to: '/events/$eventId/checkout',
          params: { eventId },
          search: {},
          replace: true,
        });
      }
    }
  }

  async function hydrateFromStoredSession(cancelled: boolean) {
    const { sessionId: storedSessionId, expiresAt } = getStoredCheckoutSession();

    if (!storedSessionId) {
      if (buyerEmail) await findActiveSessionForBuyer(cancelled);
      return;
    }

    if (expiresAt) {
      const parsedExpiry = new Date(expiresAt);
      if (!Number.isNaN(parsedExpiry.getTime()) && parsedExpiry.getTime() <= Date.now()) {
        clearStoredCheckoutSession();
        return;
      }
    }

    try {
      const status = await getPublicCheckoutSessionStatus(storedSessionId);
      if (cancelled) return;

      const sessionStatus = getCheckoutStatusValue(status);
      const paymentStatus = getCheckoutPaymentStatus(status);

      if (sessionStatus === 'complete') {
        clearStoredCheckoutSession();
        setCheckoutStatus(status);
        setCheckoutView('success');
        return;
      }
      if (sessionStatus === 'processing') {
        setSessionId(storedSessionId);
        setCheckoutStatus(status);
        setCheckoutView('processing');
        return;
      }
      if (sessionStatus === 'expired') {
        clearStoredCheckoutSession();
        setCheckoutStatus(status);
        setCheckoutView('failure');
        setCheckoutError('Your saved checkout session expired. Please start again.');
        return;
      }
      if (sessionStatus === 'open' && paymentStatus === 'unpaid') {
        await handleResumeSession(storedSessionId);
        return;
      }
      clearStoredCheckoutSession();
    } catch {
      clearStoredCheckoutSession();
      await findActiveSessionForBuyer(cancelled);
    }
  }

  async function findActiveSessionForBuyer(cancelled: boolean) {
    if (!buyerEmail && !storedCustomer.email) return;
    try {
      const response = await checkActiveCheckoutSession({
        email: buyerEmail || storedCustomer.email,
        eventUuid: event.event_uuid,
      });
      if (cancelled) return;

      const activeSession = getCheckoutActiveSession(response);
      const hasActiveSession = response.hasActiveSession ?? response.has_active_session;
      if (hasActiveSession && activeSession) setActiveSessionInfo(activeSession);
    } catch {
      setActiveSessionInfo(null);
    }
  }

  async function handleCancelSession() {
    const targetSessionId = activeSessionInfo?.session_id ?? sessionId;
    if (!targetSessionId && !buyerEmail) {
      setActiveSessionInfo(null);
      clearStoredCheckoutSession();
      setCheckoutView('selection');
      return;
    }

    setIsSubmitting(true);
    try {
      await cancelActiveCheckoutSession({
        email: buyerEmail || activeSessionInfo?.email || undefined,
        session_id: targetSessionId ?? undefined,
      });
      resetSessionState();
      setCheckoutView('selection');
      await refreshAvailability();
    } catch (err) {
      const message = extractApiError(err, 'Failed to cancel the session. Please try again.');
      setCheckoutError(`Could not cancel session: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleExpiredReservation() {
    clearStoredCheckoutSession();
    setSessionId(null);
    setClientSecret(null);
    setStripePromise(null);
    setReservationExpiresAt(null);
    setCheckoutView('failure');
    setCheckoutError('Your ticket reservation expired before payment completed.');
  }

  async function pollCheckoutStatus(currentSessionId: string) {
    try {
      const status = await getEmbeddedCheckoutSessionStatus(currentSessionId);
      const sessionStatus = getCheckoutStatusValue(status);

      if (sessionStatus === 'complete') {
        const emailForCleanup = buyerEmail || storedCustomer.email;
        if (emailForCleanup || currentSessionId) {
          cancelActiveCheckoutSession({
            email: emailForCleanup || undefined,
            session_id: currentSessionId,
          }).catch(() => {
            /* best-effort */
          });
        }
        clearStoredCheckoutSession();
        setCheckoutStatus(status);
        setCheckoutView('success');
        return;
      }
      if (sessionStatus === 'processing') setCheckoutStatus(status);
      if (sessionStatus === 'expired') {
        setCheckoutStatus(status);
        handleExpiredReservation();
      }
    } catch {
      setCheckoutError(
        'We could not verify your payment yet. You can refresh the status or reopen this page.',
      );
    }
  }

  async function handleCheckout() {
    if (isSubmitting) return;
    if (!validateCheckout()) return;

    setCheckoutError(null);

    if (!executeRecaptcha) {
      setCheckoutError('reCAPTCHA is not ready yet. Please try again in a moment.');
      return;
    }

    let recaptchaToken: string;
    try {
      recaptchaToken = await executeRecaptcha('checkout');
    } catch {
      setCheckoutError('reCAPTCHA verification failed. Please try again.');
      return;
    }

    if (allSelectedTicketsFree && event.event_uuid) {
      try {
        const ticketIds = selectedTickets.map((t) => t.id!);
        const eligibility = await checkFreeBookingEligibility(event.event_uuid, ticketIds);
        if (!eligibility.all_free) {
          setCheckoutError(
            eligibility.message ||
              'One or more selected tickets are not eligible for free booking.',
          );
          return;
        }
      } catch (eligError) {
        if (eligError instanceof Error && eligError.message) {
          setCheckoutError(eligError.message);
          return;
        }
      }
    }

    setIsSubmitting(true);
    storeCheckoutCustomer({ email: buyerEmail, name: buyerName });

    try {
      if (allSelectedTicketsFree) {
        if (!event.event_uuid) {
          throw new Error('This event is missing a public booking identifier.');
        }

        const response = await createPublicFreeBooking(event.event_uuid, {
          tickets: selectedTickets.map((ticket) => ({
            ticket_id: ticket.id!,
            quantity: ticketCounts[ticket.id!] ?? 0,
          })),
          customer_email: buyerEmail,
          customer_name: buyerName,
          recipients: buildApiRecipients(),
          recaptcha_token: recaptchaToken,
          invite_token: inviteToken ?? null,
          ...(appliedPromoCode ? { promo_code: appliedPromoCode } : {}),
        });

        if (response.success === false) {
          throw new Error(response.message || 'Free booking could not be completed.');
        }

        setCheckoutStatus({
          status: 'complete',
          event_title: eventTitle,
          tickets: selectedTickets.map((ticket) => ({
            ticket_type: ticket.ticket_type || 'General Admission',
            quantity: ticketCounts[ticket.id!] ?? 0,
          })),
          total_amount: 0,
        });
        setCheckoutView('success');
        clearStoredCheckoutSession();
        setIsSubmitting(false);
        return;
      }

      const paidResponse = await createPublicCheckoutSession({
        event_id: event.id,
        tickets: selectedTickets.map((ticket) => ({
          id: ticket.id!,
          quantity: ticketCounts[ticket.id!] ?? 0,
        })),
        email: buyerEmail,
        name: buyerName,
        recipients: buildApiRecipients(),
        recaptcha_token: recaptchaToken,
        return_url: buildPaymentReturnUrl(event.id),
        ...(inviteToken ? { invite_token: inviteToken } : {}),
        ...(appliedPromoCode
          ? {
              promo_code: appliedPromoCode,
              discount_amount: parseFloat(promoDiscountAmount.toFixed(2)),
              discounted_subtotal: parseFloat(discountedSubtotal.toFixed(2)),
            }
          : {}),
      });

      const activeSession = getCheckoutActiveSession(paidResponse);
      if (paidResponse.success === false && activeSession) {
        setActiveSessionInfo(activeSession);
        setCheckoutError(null);
        return;
      }

      await startEmbeddedCheckout(paidResponse);
    } catch (error: unknown) {
      const apiError = parseApiError(error);
      if (apiError.status === 409) {
        const rawData = apiError.data as
          | {
              active_session?: ActiveSessionInfo;
              activeSession?: ActiveSessionInfo;
              session_id?: string;
              email?: string;
              event_id?: number;
            }
          | undefined;
        const activeSession: ActiveSessionInfo | null =
          rawData?.active_session ??
          rawData?.activeSession ??
          (rawData?.session_id
            ? { session_id: rawData.session_id, email: rawData.email, event_id: rawData.event_id }
            : null);
        if (activeSession?.session_id) {
          setActiveSessionInfo(activeSession);
          setCheckoutError(null);
        } else {
          setCheckoutError(
            extractApiError(error, 'You already have a checkout session in progress.'),
          );
        }
      } else {
        setCheckoutError(extractApiError(error, 'Checkout could not be started.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleShareEvent() {
    const url = `${window.location.origin}/events/${eventId}`;
    const copied = await copyTextToClipboard(url);
    if (copied) {
      toast.success('Link copied');
    } else {
      toast.error('Unable to copy link. Please copy manually.');
    }
  }

  const assignedToRecipients = recipients.reduce((sum, r) => sum + r.quantity, 0);
  const ticketsRemaining = Math.max(0, totalTickets - assignedToRecipients);
  const assignProgressPct =
    totalTickets > 0 ? Math.round((assignedToRecipients / totalTickets) * 100) : 0;

  // Group recipient entries by email so the same person's multiple ticket
  // types render inside a single card instead of one card per entry.
  const recipientGroups = useMemo(() => {
    const groups: RecipientEntry[][] = [];
    const indexByEmail = new Map<string, number>();
    for (const recipient of recipients) {
      const key = recipient.email.trim().toLowerCase();
      const existingIndex = key ? indexByEmail.get(key) : undefined;
      if (existingIndex !== undefined) {
        groups[existingIndex].push(recipient);
      } else {
        if (key) indexByEmail.set(key, groups.length);
        groups.push([recipient]);
      }
    }
    return groups;
  }, [recipients]);

  function openAddTicketForPerson(person: RecipientEntry) {
    setDraftRecipient({
      id: Date.now(),
      name: person.name,
      email: person.email,
      ticketId: null,
      quantity: 1,
    });
    setDraftRecipientError(null);
    setIsRecipientFormOpen(true);
  }

  const images = (event.medias ?? [])
    .filter((m) => m.media_type === 'image' || m.image || m.file_url)
    .map((m) => m.file_url ?? m.image ?? m.file ?? '')
    .filter(Boolean);
  const thumbnail = images[0] ?? EVENT_IMAGE_FALLBACK_SRC;

  const deliveredCount =
    checkoutStatus?.tickets?.reduce((sum, t) => sum + (t.quantity ?? 0), 0) ?? totalTickets;

  // ── Processing / success / failure — full centered states, no breadcrumb ──
  if (!isRecovering && checkoutView === 'processing') {
    return (
      <StatusShell>
        <div className="w-18 h-18 rounded-full bg-blue-500/10 text-blue-300 flex items-center justify-center">
          <FiLoader className="animate-spin" size={34} />
        </div>
        <div className="space-y-2 max-w-xl">
          <h1 className="text-white text-2xl sm:text-3xl font-bold">Payment processing</h1>
          <p className="text-zinc-400">
            Your payment has been submitted and is still being confirmed. You can safely refresh
            this page or check again below.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-full border-white/10 hover:bg-white/5"
          onClick={() => void pollCheckoutStatus(sessionId ?? '')}
          disabled={!sessionId}
        >
          Check again
        </Button>
      </StatusShell>
    );
  }

  if (!isRecovering && checkoutView === 'success') {
    return (
      <StatusShell wide>
        <div className="w-20 h-20 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
          <FiCheckCircle size={40} />
        </div>
        <div className="space-y-2">
          <h1 className="text-white text-3xl font-bold">You're all set! 🎉</h1>
          <p className="text-zinc-400">Your tickets have been confirmed.</p>
        </div>

        <div className="w-full rounded-3xl border border-white/8 bg-zinc-950 p-5 flex flex-col sm:flex-row items-center gap-5 text-left">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <img
              src={thumbnail}
              alt={eventTitle}
              className="w-20 h-20 rounded-2xl object-cover shrink-0 bg-zinc-900"
              onError={(e) => {
                e.currentTarget.src = EVENT_IMAGE_FALLBACK_SRC;
              }}
            />
            <div className="min-w-0">
              <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium">
                Event
              </p>
              <p className="text-white font-semibold truncate">{eventTitle}</p>
              {event.start && (
                <>
                  <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium mt-3">
                    Date and Time
                  </p>
                  <p className="text-zinc-300 text-sm">
                    {formatDate(event.start, event.timezone, true)} ·{' '}
                    {formatTime(event.start, event.timezone, true)}
                  </p>
                </>
              )}
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-zinc-900 border border-white/8 px-3 py-1.5">
                <FiCheck className="text-emerald-400 shrink-0" size={13} />
                <span className="text-xs text-zinc-300">
                  {deliveredCount} ticket{deliveredCount === 1 ? '' : 's'} delivered — all ticket
                  holders have been notified
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:w-48 shrink-0 justify-center">
            <button
              type="button"
              onClick={() => void navigate({ to: '/tickets', search: { eventId } })}
              className="rounded-full bg-white text-black font-semibold px-5 py-2.5 text-sm inline-flex items-center justify-center gap-2 hover:bg-zinc-200 transition-colors"
            >
              View Ticket
              <FiArrowRight size={14} />
            </button>
            <button
              type="button"
              onClick={() => void handleShareEvent()}
              className="rounded-full bg-zinc-800 border border-white/10 text-white font-semibold px-5 py-2.5 text-sm inline-flex items-center justify-center gap-2 hover:bg-zinc-700 transition-colors"
            >
              <FiShare2 size={14} />
              Share Event
            </button>
          </div>
        </div>

        <div className="w-full relative overflow-hidden rounded-3xl border border-white/8 p-5 bg-linear-to-br from-indigo-950 via-zinc-950 to-black text-left">
          <p className="text-white text-[15px] font-semibold mb-1">
            Inspired to host your own event?
          </p>
          <p className="text-zinc-400 text-[13px] mb-4">
            Bring your next event to life with Musicade.
          </p>
          <Link
            to="/events/create"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-[13px] font-semibold hover:bg-zinc-200 transition-colors"
          >
            Start Creating
            <FiArrowRight size={14} />
          </Link>
        </div>
      </StatusShell>
    );
  }

  if (!isRecovering && checkoutView === 'failure') {
    return (
      <StatusShell>
        <div className="w-18 h-18 rounded-full bg-red-500/10 text-red-300 flex items-center justify-center">
          <FiXCircle size={34} />
        </div>
        <div className="space-y-2 max-w-xl">
          <h1 className="text-white text-2xl sm:text-3xl font-bold">Payment incomplete</h1>
          <p className="text-zinc-400">
            The payment did not complete, or the reservation expired before confirmation. You can
            retry checkout with the latest inventory below.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button
            variant="outline"
            className="rounded-full border-white/10 hover:bg-white/5"
            onClick={() => {
              setCheckoutView('selection');
              setCheckoutError(null);
              void refreshAvailability();
            }}
          >
            Try again
          </Button>
          <Button className="rounded-full" onClick={goToEvent}>
            Back to event
          </Button>
        </div>
      </StatusShell>
    );
  }

  // ── Selection / embedded payment ────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white pb-32" style={eventAccent.style}>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-32 pt-[calc(var(--header-height)+1rem)]">
        {/* Breadcrumb + step indicator */}
        <div className="flex items-center gap-2 text-sm mb-6">
          <button
            type="button"
            onClick={goToEvent}
            className="flex items-center gap-1.5 text-zinc-300 font-semibold italic hover:text-white transition-colors"
          >
            <FiArrowLeft size={15} />
            Back to Ticket Selection
          </button>
          <FiChevronRight size={13} className="text-zinc-600 shrink-0" />
          <span className="text-zinc-500 italic">Checkout</span>
        </div>

        <div className="flex items-center gap-3 mb-8 pb-6 border-b border-white/8">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-white text-black flex items-center justify-center">
              <FiCheck size={14} />
            </div>
            <span className="text-white text-sm font-semibold">Tickets</span>
          </div>
          <div className="w-10 sm:w-16 h-px bg-white/15" />
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-(--event-accent) text-(--event-accent-fg) flex items-center justify-center text-xs font-bold">
              2
            </div>
            <span className="text-[color-mix(in_oklab,var(--event-accent)_75%,white)] text-sm font-semibold">
              Checkout
            </span>
          </div>
        </div>

        {isRecovering ? (
          <div className="py-24 flex flex-col items-center justify-center gap-4 text-center">
            <div className="w-12 h-12 rounded-full border-2 border-white/10 border-t-white animate-spin" />
            <div>
              <p className="text-white text-lg font-semibold">Restoring your checkout</p>
              <p className="text-zinc-400 text-sm">
                Checking ticket availability and recovering any active session.
              </p>
            </div>
          </div>
        ) : (
          <>
            {activeSessionInfo && checkoutView === 'selection' && (
              <div className="mb-6 rounded-3xl border border-amber-500/25 bg-amber-500/8 px-4 py-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-amber-300 font-semibold text-sm">
                      You already have a checkout in progress
                    </p>
                    <p className="text-zinc-300 text-sm mt-1">
                      Resume the existing session to avoid losing reserved tickets, or cancel it
                      before starting a new payment.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      className="rounded-full border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
                      onClick={() => void handleCancelSession()}
                      isLoading={isSubmitting}
                    >
                      Cancel session
                    </Button>
                    <Button
                      className="rounded-full bg-amber-300 text-black hover:bg-amber-200"
                      onClick={() => void handleResumeSession(activeSessionInfo.session_id)}
                      isLoading={isSubmitting}
                    >
                      Resume checkout
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {checkoutError && (
              <div className="mb-6 rounded-2xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-start gap-3">
                <FiAlertCircle className="mt-0.5 shrink-0" size={16} />
                <span>{checkoutError}</span>
              </div>
            )}

            {(salesClosed || hasAvailabilityConflict) && checkoutView === 'selection' && (
              <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/8 px-4 py-3 text-sm text-amber-200">
                <FiAlertCircle className="mt-0.5 shrink-0" size={16} />
                <span>
                  {salesClosed
                    ? salesClosedReason.notStarted
                      ? `Tickets on sale from ${new Date(salesClosedReason.notStarted).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`
                      : 'Ticket sales have ended for this selection.'
                    : 'One or more selected tickets are no longer available in this quantity. Go back to adjust your selection.'}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_380px] gap-6 items-start">
              {/* ── Left column ── */}
              <div className="space-y-6 min-w-0">
                {checkoutView === 'selection' ? (
                  totalTickets === 0 ? (
                    <div className="rounded-3xl border border-white/8 bg-zinc-950 p-8 text-center">
                      <p className="text-white font-semibold mb-2">No tickets selected</p>
                      <p className="text-zinc-400 text-sm mb-5">
                        Head back to the event to choose your tickets first.
                      </p>
                      <Button className="rounded-full" onClick={goToEvent}>
                        Back to Ticket Selection
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Buyer Information */}
                      <section className="rounded-3xl border border-white/8 bg-zinc-950 p-5 sm:p-6">
                        <h2 className="text-white text-lg font-bold mb-5">Buyer Information</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            label="Full Name"
                            value={buyerName}
                            onChange={(e) => setBuyerName(e.target.value)}
                            placeholder="Jane Doe"
                          />
                          <FormField
                            label="Email Address"
                            type="email"
                            value={buyerEmail}
                            onChange={(e) => setBuyerEmail(e.target.value)}
                            placeholder="jane@example.com"
                          />
                        </div>
                        <p className="mt-4 text-xs leading-relaxed text-zinc-500">
                          All purchased tickets are automatically assigned to you unless you choose
                          to assign them to someone else.
                        </p>
                      </section>

                      {/* Recipients */}
                      <section className="rounded-3xl border border-white/8 bg-zinc-950 p-5 sm:p-6">
                        <h2 className="text-white text-lg font-bold">Buying for someone else?</h2>
                        <p className="text-zinc-500 text-sm mt-1">
                          Add the details of anyone else who should receive a ticket
                        </p>

                        {totalTickets > 0 && (
                          <div className="mt-5">
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="text-white font-medium">
                                {ticketsRemaining} of {totalTickets} Tickets Remaining
                              </span>
                              <span className="text-[color-mix(in_oklab,var(--event-accent)_75%,white)] font-semibold">
                                {assignProgressPct}%
                              </span>
                            </div>
                            <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                              <div
                                className="h-full bg-(--event-accent) rounded-full transition-all"
                                style={{ width: `${assignProgressPct}%` }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="mt-5 space-y-3">
                          {recipientGroups.map((group) => {
                            const groupEmail = group[0].email.trim().toLowerCase();
                            const draftBelongsToGroup =
                              isRecipientFormOpen &&
                              draftRecipient &&
                              !recipients.some((r) => r.id === draftRecipient.id) &&
                              groupEmail &&
                              draftRecipient.email.trim().toLowerCase() === groupEmail;

                            return (
                              <div
                                key={group[0].id}
                                className="rounded-2xl border border-white/8 bg-zinc-900/40 p-3 space-y-2"
                              >
                                {group.map((recipient) =>
                                  isRecipientFormOpen && draftRecipient?.id === recipient.id ? (
                                    <RecipientForm
                                      key={recipient.id}
                                      draft={draftRecipient}
                                      onChange={setDraftRecipient}
                                      error={draftRecipientError}
                                      tickets={selectedTickets}
                                      ticketCounts={ticketCounts}
                                      otherRecipients={recipients.filter(
                                        (r) => r.id !== recipient.id,
                                      )}
                                      onCancel={() => handleRemoveRecipient(recipient.id)}
                                      onSave={handleSaveRecipient}
                                      onSaveAndAddAnother={handleSaveAndAddAnother}
                                    />
                                  ) : (
                                    <RecipientView
                                      key={recipient.id}
                                      recipient={recipient}
                                      ticketLabel={
                                        tickets.find((t) => t.id === recipient.ticketId)
                                          ?.ticket_type ?? 'Unassigned'
                                      }
                                      compact
                                      onEdit={() => openEditRecipientForm(recipient)}
                                      onRemove={() => handleRemoveRecipient(recipient.id)}
                                    />
                                  ),
                                )}

                                {draftBelongsToGroup && draftRecipient && (
                                  <RecipientForm
                                    draft={draftRecipient}
                                    onChange={setDraftRecipient}
                                    error={draftRecipientError}
                                    tickets={selectedTickets}
                                    ticketCounts={ticketCounts}
                                    otherRecipients={recipients}
                                    onCancel={closeRecipientForm}
                                    onSave={handleSaveRecipient}
                                    onSaveAndAddAnother={handleSaveAndAddAnother}
                                  />
                                )}

                                {!isRecipientFormOpen && (
                                  <button
                                    type="button"
                                    onClick={() => openAddTicketForPerson(group[0])}
                                    disabled={assignedToRecipients >= totalTickets}
                                    className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    + Add another ticket for this person
                                  </button>
                                )}
                              </div>
                            );
                          })}

                          {isRecipientFormOpen &&
                            draftRecipient &&
                            !recipients.some((r) => r.id === draftRecipient.id) &&
                            !(
                              draftRecipient.email.trim() &&
                              recipientGroups.some(
                                (group) =>
                                  group[0].email.trim().toLowerCase() ===
                                  draftRecipient.email.trim().toLowerCase(),
                              )
                            ) && (
                              <RecipientForm
                                draft={draftRecipient}
                                onChange={setDraftRecipient}
                                error={draftRecipientError}
                                tickets={selectedTickets}
                                ticketCounts={ticketCounts}
                                otherRecipients={recipients}
                                onCancel={closeRecipientForm}
                                onSave={handleSaveRecipient}
                                onSaveAndAddAnother={handleSaveAndAddAnother}
                              />
                            )}
                        </div>

                        {!isRecipientFormOpen && (
                          <button
                            type="button"
                            onClick={openAddRecipientForm}
                            disabled={assignedToRecipients >= totalTickets}
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed px-5 py-2.5 text-sm font-semibold transition-[filter]"
                          >
                            <FiUserPlus size={15} />
                            Add Recipient
                          </button>
                        )}
                      </section>
                    </>
                  )
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-col gap-2 rounded-[28px] border border-blue-500/20 bg-blue-500/8 p-4 sm:p-5 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-blue-300">Payment in progress</p>
                        <p className="text-sm text-zinc-300 mt-1">
                          Complete payment below before the timer runs out, or your held tickets
                          will be released.
                        </p>
                      </div>
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${timeRemainingMs < 180000 ? 'bg-amber-500/15 text-amber-200 border border-amber-500/20' : 'bg-white/5 text-white border border-white/10'}`}
                      >
                        <FiClock size={16} />
                        {formatTimeRemaining(timeRemainingMs)}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-white/8 bg-zinc-900/60 p-2 sm:p-4 min-h-130">
                      {stripePromise && clientSecret ? (
                        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                          <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                      ) : (
                        <div className="flex min-h-105 flex-col items-center justify-center gap-3 text-center text-zinc-400">
                          <FiLoader className="animate-spin" size={24} />
                          <p>Loading secure checkout…</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Right column: Order Summary ── */}
              <aside className="rounded-3xl border border-white/8 bg-zinc-950 p-5 sm:p-6 xl:sticky xl:top-24">
                <h2 className="text-white text-lg font-bold mb-4">Order Summary</h2>

                <div className="space-y-3 mb-4">
                  {selectedTickets.length > 0 ? (
                    selectedTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="flex items-start justify-between gap-3 text-sm"
                      >
                        <span className="text-zinc-300">
                          {ticket.ticket_type || 'General Admission'} ×{' '}
                          {ticketCounts[ticket.id!] ?? 0}{' '}
                          <span className="text-zinc-500">
                            ({ticket.price === 0 ? 'Free' : `${formatCurrency(ticket.price)} each`})
                          </span>
                        </span>
                        <span className="text-white font-semibold whitespace-nowrap">
                          {ticket.price === 0
                            ? 'Free'
                            : formatCurrency(ticket.price * (ticketCounts[ticket.id!] ?? 0))}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-zinc-500">No tickets selected yet.</p>
                  )}
                </div>

                <div className="h-px bg-white/8 my-4" />

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between text-zinc-400">
                    <span>Subtotal</span>
                    <span className="text-zinc-200">{formatCurrency(summary.subtotal)}</span>
                  </div>
                  {appliedPromoCode && promoDiscountAmount > 0 && (
                    <div className="flex items-center justify-between text-emerald-400">
                      <span className="flex items-center gap-1.5">
                        <FiTag size={12} />
                        Promo ({appliedPromoCode})
                      </span>
                      <span>-{formatCurrency(promoDiscountAmount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-zinc-400">
                    <span>Platform Fee (7%)</span>
                    <span className="text-zinc-200">
                      {formatCurrency(discountedSummary.platformFee)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-zinc-400">
                    <span>Processing Fee</span>
                    <span className="text-zinc-200">
                      {formatCurrency(discountedSummary.processingFee)}
                    </span>
                  </div>
                </div>

                <div className="h-px bg-white/8 my-4" />

                <div className="flex items-center justify-between mb-5">
                  <span className="text-white font-bold">Total Amount</span>
                  <span className="text-[color-mix(in_oklab,var(--event-accent)_75%,white)] text-xl font-bold">
                    {allSelectedTicketsFree ? 'Free' : formatCurrency(discountedSummary.total)}
                  </span>
                </div>

                {!allSelectedTicketsFree && checkoutView === 'selection' && (
                  <div className="rounded-2xl border border-white/8 bg-black/30 p-4 space-y-2 mb-5">
                    {!appliedPromoCode ? (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <FiTag
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                              size={14}
                            />
                            <input
                              type="text"
                              value={promoCode}
                              onChange={(e) => {
                                setPromoCode(e.target.value.toUpperCase());
                                setPromoError(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void handleValidatePromoCode();
                              }}
                              placeholder="Promo code"
                              disabled={promoValidating}
                              className="w-full rounded-2xl border border-white/10 bg-black/40 pl-9 pr-4 py-2.5 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-(--event-accent)/50 disabled:opacity-60"
                            />
                          </div>
                          <Button
                            variant="outline"
                            className="rounded-full border-white/10 hover:bg-white/5 shrink-0"
                            onClick={() => void handleValidatePromoCode()}
                            disabled={!promoCode.trim() || promoValidating}
                            isLoading={promoValidating}
                          >
                            Apply
                          </Button>
                        </div>
                        {promoError && (
                          <p className="flex items-center gap-1.5 text-xs text-red-400">
                            <FiAlertCircle size={12} />
                            {promoError}
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm text-emerald-400">
                          <FiCheckCircle size={15} />
                          <span className="font-semibold">{appliedPromoCode}</span>
                          <span className="text-zinc-400">applied</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemovePromoCode}
                          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white transition-colors"
                        >
                          <FiX size={13} />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {checkoutView === 'selection' && (
                  <div className="space-y-2 mb-5">
                    <label className="flex items-start gap-2.5 text-xs text-zinc-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailOptInOrganizer}
                        onChange={(e) => setEmailOptInOrganizer(e.target.checked)}
                        className="mt-0.5 accent-(--event-accent)"
                      />
                      Email me about future events and presales from this organizer
                    </label>
                    <label className="flex items-start gap-2.5 text-xs text-zinc-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailOptInMusicade}
                        onChange={(e) => setEmailOptInMusicade(e.target.checked)}
                        className="mt-0.5 accent-(--event-accent)"
                      />
                      Email me about new features, promotions, and Musicade updates
                    </label>
                  </div>
                )}

                {checkoutView === 'selection' && (
                  <button
                    type="button"
                    onClick={() => void handleCheckout()}
                    disabled={
                      selectedTickets.length === 0 ||
                      isSubmitting ||
                      salesClosed ||
                      hasAvailabilityConflict
                    }
                    className="w-full rounded-full bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed font-bold py-3.5 flex items-center justify-center gap-2 transition-[filter]"
                  >
                    {isSubmitting ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FiLock size={15} />
                    )}
                    {salesClosed
                      ? salesClosedReason.notStarted
                        ? 'Sales not started'
                        : 'Sales ended'
                      : allSelectedTicketsFree
                        ? 'Book free tickets'
                        : `Pay ${formatCurrency(discountedSummary.total)}`}
                  </button>
                )}
              </aside>
            </div>
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

// ─── Presentational sub-components ─────────────────────────────────────────

function StatusShell({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />
      <main
        className={`mx-auto px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height)+1rem)] flex flex-col items-center text-center gap-5 ${wide ? 'max-w-2xl' : 'max-w-xl'}`}
      >
        {children}
      </main>
      <BottomNav />
    </div>
  );
}

function RecipientView({
  recipient,
  ticketLabel,
  compact,
  onEdit,
  onRemove,
}: {
  recipient: RecipientEntry;
  ticketLabel: string;
  compact?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3.5 ${
        compact ? 'rounded-xl bg-zinc-900/60' : 'rounded-2xl border border-white/8 bg-zinc-900/60'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={recipient.name || recipient.email} size="sm" />
        <div className="min-w-0">
          <p className="text-white font-semibold text-sm truncate">{recipient.name}</p>
          <p className="text-zinc-500 text-xs truncate">{recipient.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="rounded-full bg-zinc-800 border border-white/10 px-2.5 py-1 text-[11px] font-medium text-zinc-300 whitespace-nowrap">
          {ticketLabel} ×{recipient.quantity}
        </span>
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${recipient.name}`}
          className="w-8 h-8 rounded-full border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-center"
        >
          <FiEdit2 size={13} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${recipient.name}`}
          className="w-8 h-8 rounded-full border border-white/10 text-zinc-400 hover:text-red-300 hover:bg-red-500/10 transition-colors flex items-center justify-center"
        >
          <FiTrash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function RecipientForm({
  draft,
  onChange,
  error,
  tickets,
  ticketCounts,
  otherRecipients,
  onCancel,
  onSave,
  onSaveAndAddAnother,
}: {
  draft: RecipientEntry;
  onChange: (draft: RecipientEntry) => void;
  error: string | null;
  tickets: EventTicket[];
  ticketCounts: Record<number, number>;
  otherRecipients: RecipientEntry[];
  onCancel: () => void;
  onSave: () => void;
  onSaveAndAddAnother: () => void;
}) {
  const selectedCount = draft.ticketId ? (ticketCounts[draft.ticketId] ?? 0) : 0;
  const usedByOthers = draft.ticketId
    ? otherRecipients
        .filter((r) => r.ticketId === draft.ticketId)
        .reduce((sum, r) => sum + r.quantity, 0)
    : 0;
  const maxQuantity = Math.max(0, selectedCount - usedByOthers);

  const normalizedDraftEmail = draft.email.trim().toLowerCase();
  const hasSiblingEntries =
    !!normalizedDraftEmail &&
    otherRecipients.some((r) => r.email.trim().toLowerCase() === normalizedDraftEmail);

  return (
    <div className="rounded-2xl border border-(--event-accent)/40 bg-zinc-900/60 p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        {hasSiblingEntries && (
          <p className="text-xs text-zinc-400">
            Adding another ticket for <span className="text-white font-medium">{draft.name}</span>
          </p>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-red-500/25 bg-red-500/10 text-red-300 px-3 py-1.5 text-xs font-medium hover:bg-red-500/20 transition-colors"
        >
          <FiTrash2 size={12} />
          Remove
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-2xl border border-red-500/25 bg-red-500/10 px-3 py-2.5 text-sm text-red-200">
          <FiAlertCircle className="mt-0.5 shrink-0" size={15} />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <FormField
          label="Full Name"
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          placeholder="Jane Doe"
          disabled={hasSiblingEntries}
        />
        <FormField
          label="Email Address"
          type="email"
          value={draft.email}
          onChange={(e) => onChange({ ...draft, email: e.target.value })}
          placeholder="jane@example.com"
          disabled={hasSiblingEntries}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <span className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            Ticket Type
          </span>
          <select
            value={draft.ticketId ?? ''}
            onChange={(e) => {
              const nextTicketId = e.target.value ? Number(e.target.value) : null;
              const nextSelectedCount = nextTicketId ? (ticketCounts[nextTicketId] ?? 0) : 0;
              const nextUsedByOthers = nextTicketId
                ? otherRecipients
                    .filter((r) => r.ticketId === nextTicketId)
                    .reduce((sum, r) => sum + r.quantity, 0)
                : 0;
              const nextMaxQuantity = Math.max(0, nextSelectedCount - nextUsedByOthers);
              onChange({
                ...draft,
                ticketId: nextTicketId,
                quantity: Math.min(draft.quantity, Math.max(1, nextMaxQuantity)),
              });
            }}
            className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-white outline-none focus:border-(--event-accent)/50 appearance-none"
          >
            <option value="">Select a ticket type</option>
            {tickets.map((ticket) => (
              <option key={ticket.id} value={ticket.id!}>
                {ticket.ticket_type || 'General Admission'}
                {ticket.price > 0 ? ` – ${formatCurrency(ticket.price)}` : ' – Free'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            Quantity
          </span>
          <input
            type="number"
            min={1}
            max={Math.max(1, maxQuantity)}
            step={1}
            value={draft.quantity}
            disabled={!draft.ticketId}
            onChange={(e) => {
              const raw = Math.round(Number(e.target.value));
              const bounded = Math.min(
                Math.max(1, Number.isFinite(raw) ? raw : 1),
                Math.max(1, maxQuantity),
              );
              onChange({ ...draft, quantity: bounded });
            }}
            className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-white outline-none placeholder:text-zinc-600 focus:border-(--event-accent)/50 disabled:opacity-60"
          />
          {draft.ticketId && (
            <p className="mt-1.5 text-[11px] text-zinc-500">
              Up to {maxQuantity} available for this ticket type
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-semibold hover:bg-zinc-200 transition-colors"
        >
          <FiCheck size={14} />
          Save
        </button>
        <button
          type="button"
          onClick={onSaveAndAddAnother}
          className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm font-medium transition-colors"
        >
          + Add another ticket for this person
        </button>
      </div>
    </div>
  );
}
